import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import type { Trip } from "../../types";
import { formatMad } from "../../internal/format";
import { useMadFormatter } from "../../config";
import { dayBookingPlan, type OfferLookup } from "../../lib/trip-booking";
import { DEFAULT_TRIP_PEOPLE } from "../../state/trip-storage";
import { partyLabel, partyOf } from "../../lib/trip-party";
import { buildShareText, type ResolveItem } from "./trip-metrics";
import { dayIso, tripStartOf } from "./trip-date";
import type { TripStayPrices } from "../../hooks/use-trip-stays";

/**
 * Per-day subtotals, the total due, and share. The phone's `TripTotalsCard`,
 * same content and same order.
 *
 * It closes the page rather than opening it: a total is what you check once
 * the days are built, and putting it at the top made the trip look like a
 * bill before it was a plan.
 *
 * **Never a checkout.** An activity is paid on arrival, so this quotes what is
 * due on the day and offers no way to pay it. A day with only explore stops
 * says "no bookings" rather than a dishonest 0.
 *
 * Each day's amount is the one its own booking block shows (`dayBookingPlan`,
 * for the trip's party), so the total is the sum of the days above it and of
 * the bucket lines the days would add. Restaurants and stays are not in it:
 * tables are free and stays are paid online.
 */
export function TripTotalsCard({
  trip,
  resolveItem,
  offerFor,
  stays,
}: {
  trip: Trip;
  resolveItem: ResolveItem;
  offerFor?: OfferLookup;
  /** Where the guest sleeps: listed apart, paid online, never due on the day. */
  stays?: TripStayPrices;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const format = useMadFormatter();
  const money = (mad: number) => (format ? format(mad) : formatMad(mad, lang));
  const [copied, setCopied] = useState(false);
  const party = partyOf(trip, DEFAULT_TRIP_PEOPLE);
  const plans = trip.days.map((d, i) =>
    dayBookingPlan(d, resolveItem, party, dayIso(tripStartOf(trip), i), offerFor),
  );
  const total = plans.reduce((sum, p) => sum + p.total, 0);
  const exact = plans.every((p) => p.exact || p.activities.length === 0);
  const shown = (mad: number, isExact: boolean) =>
    isExact ? money(mad) : t("trip.price.from", { price: money(mad) });

  const onShare = async () => {
    const text = buildShareText(
      trip,
      resolveItem,
      { due: t("trip.overview.due"), payNote: t("trip.overview.pay_note") },
      () => shown(total, exact),
      total,
    );
    // The phone opens the OS share sheet. A browser only has one where
    // `navigator.share` exists (every phone browser, some desktops), so the
    // desktop fallback is the clipboard rather than nothing happening.
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: trip.name, text });
        return;
      } catch {
        // Dismissing the sheet throws. That is a choice, not a failure.
        return;
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* Clipboard blocked; nothing useful to say. */
    }
  };

  return (
    <div className="mj-trip-totals" data-testid="trip-totals-card">
      {trip.days.map((d, i) => {
        const plan = plans[i]!;
        const due = plan.total;
        return (
          <div
            key={d.id}
            className={`mj-trip-totals__day${i > 0 ? " has-rule" : ""}`}
          >
            <div className="mj-trip-totals__day-name">
              <strong>{d.label}</strong>
              <span>{t("trip.stop_count", { count: d.stops.length })}</span>
            </div>
            {due > 0 ? (
              <span className="mj-trip-totals__day-due">
                {shown(due, plan.exact)}
              </span>
            ) : (
              <span className="mj-trip-totals__day-none">
                {t("trip.totals.no_bookings")}
              </span>
            )}
          </div>
        );
      })}

      <div className="mj-trip-totals__total">
        <span className="mj-trip-totals__total-label">
          {party.childAges.length > 0
            ? t("trip.book.due_party", { party: partyLabel(t, party) })
            : t("trip.book.due_people", { count: party.adults })}
        </span>
        <span className="mj-trip-totals__total-value">
          {total > 0 ? shown(total, exact) : "-"}
        </span>
      </div>
      <p className="mj-trip-totals__note">{t("trip.totals.extras_note")}</p>

      {stays && stays.legs.some((l) => l.stay && l.stay.kind !== "own" && l.nights > 0) ? (
        <div className="mj-trip-totals__stays" data-testid="trip-totals-stays">
          {stays.legs.map((leg, i) => {
            // The guest's own place is not ours to price or collect.
            if (!leg.stay || leg.nights < 1 || leg.stay.kind === "own") return null;
            const p = stays.prices[i]!;
            return (
              <div key={leg.dayIds[0]} className="mj-trip-totals__day has-rule">
                <div className="mj-trip-totals__day-name">
                  <strong>{leg.stay.title}</strong>
                  <span>{t("trip.stay.nights", { count: leg.nights })}</span>
                </div>
                <span className={p.state === "priced" ? "mj-trip-totals__day-due" : "mj-trip-totals__day-none"}>
                  {p.state === "priced"
                    ? p.text
                    : p.state === "loading"
                      ? t("trip.stay.pricing")
                      : p.state === "full"
                        ? t("trip.stay.full")
                        : t("trip.stay.price_on_page")}
                </span>
              </div>
            );
          })}
          {stays.total ? (
            <div className="mj-trip-totals__total">
              <span className="mj-trip-totals__total-label">{t("trip.stay.paid_online_total")}</span>
              <span className="mj-trip-totals__total-value">{stays.total}</span>
            </div>
          ) : null}
        </div>
      ) : null}

      <button type="button" className="mj-trip-totals__share" onClick={onShare}>
        <Icon name={copied ? "ri-check-line" : "ri-share-line"} aria-hidden="true" />
        {copied ? t("trip.link_copied") : t("trip.share.button")}
      </button>
    </div>
  );
}
